import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Grid,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  CalendarToday as CalendarTodayIcon,
  AttachMoney as AttachMoneyIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  AccessTime as AccessTimeIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const CollectedInstallments = () => {
  const { user } = useAuth();
  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [customerNic, setCustomerNic] = useState('');
  const [installments, setInstallments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState({ totalAmount: 0, count: 0 });
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    if (!user?.userId) return;
    
    setLoading(true);
    setError('');
    try {
      const params = {
        collectorId: user.userId,
        startDate: startDate.trim(),
        endDate: endDate.trim(),
      };
      
      if (customerNic && customerNic.trim()) {
        params.customerNic = customerNic.trim();
      }
      
      const response = await api.get('/collector/installments/collected', { params });
      
      const data = response.data || [];
      setInstallments(data);
      
      // Calculate summary
      const total = data.reduce((sum, inst) => sum + (parseFloat(inst.paidAmount) || 0), 0);
      setSummary({
        totalAmount: total,
        count: data.length,
      });
    } catch (err) {
      console.error('Error fetching collected installments:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load collected installments');
      setInstallments([]);
      setSummary({ totalAmount: 0, count: 0 });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchData();
  };

  const handleClear = () => {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    setStartDate(todayStr);
    setEndDate(todayStr);
    setCustomerNic('');
    // Fetch will be triggered by useEffect or manual call
    setTimeout(() => fetchData(), 100);
  };

  const formatCurrency = (num) => {
    if (!num && num !== 0) return 'Rs 0.00';
    return `Rs ${parseFloat(num).toFixed(2)}`;
  };

  const formatDateTime = (dateStr, timeStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      const dateFormatted = date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
      
      if (timeStr) {
        // If updatedAt is available, extract time from it
        const updatedAt = new Date(timeStr);
        const timeFormatted = updatedAt.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        });
        return `${dateFormatted} at ${timeFormatted}`;
      }
      
      return dateFormatted;
    } catch (e) {
      return dateStr;
    }
  };

  const formatTime = (dateTimeStr) => {
    if (!dateTimeStr) return 'N/A';
    try {
      const date = new Date(dateTimeStr);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      });
    } catch (e) {
      return 'N/A';
    }
  };

  const handleDownloadPdf = async () => {
    if (!user?.userId) return;
    
    if (installments.length === 0) {
      alert('No data available to generate PDF. Please search for collected installments first.');
      return;
    }
    
    setDownloadingPdf(true);
    try {
      const params = {
        collectorId: user.userId,
        startDate: startDate.trim(),
        endDate: endDate.trim(),
      };
      
      if (customerNic && customerNic.trim()) {
        params.customerNic = customerNic.trim();
      }
      
      const response = await api.get('/collector/installments/collected/pdf', {
        params,
        responseType: 'blob', // Important for binary data
      });
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `collected-installments-${startDate}-to-${endDate}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      alert(err.response?.data?.error || err.message || 'Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <Box>
      <Typography 
        variant="h4" 
        gutterBottom
        sx={{ 
          fontWeight: 700,
          color: '#0D47A1',
          mb: 3,
          fontSize: { xs: '1.75rem', sm: '2rem' },
        }}
      >
        Collected Installments
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #2E7D32 0%, #1B5E20 100%)',
              color: 'white',
              boxShadow: 3,
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AttachMoneyIcon sx={{ mr: 1, fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Total Collected
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {formatCurrency(summary.totalAmount)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #1976D2 0%, #0D47A1 100%)',
              color: 'white',
              boxShadow: 3,
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <DescriptionIcon sx={{ mr: 1, fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Total Records
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {summary.count}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3, boxShadow: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#0D47A1' }}>
          Filters
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="From Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: <CalendarTodayIcon sx={{ mr: 1, color: 'action.active' }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="To Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: <CalendarTodayIcon sx={{ mr: 1, color: 'action.active' }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Customer NIC"
              value={customerNic}
              onChange={(e) => setCustomerNic(e.target.value)}
              placeholder="Enter NIC number"
              InputProps={{
                startAdornment: <PersonIcon sx={{ mr: 1, color: 'action.active' }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                sx={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #1976D2 0%, #0D47A1 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1565C0 0%, #0A3D91 100%)',
                  },
                }}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClear}
                sx={{ flex: 1 }}
              >
                Clear
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              startIcon={<PictureAsPdfIcon />}
              onClick={handleDownloadPdf}
              disabled={downloadingPdf || loading || installments.length === 0}
              sx={{
                background: 'linear-gradient(135deg, #D32F2F 0%, #B71C1C 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #C62828 0%, #A01212 100%)',
                },
                '&:disabled': {
                  background: '#cccccc',
                },
              }}
            >
              {downloadingPdf ? 'Generating PDF...' : 'Download PDF Report'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress size={60} />
        </Box>
      ) : installments.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No collected installments found for the selected filters
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <TableContainer 
            component={Paper} 
            sx={{ 
              boxShadow: 2,
              width: '100%',
              maxWidth: '100%',
              overflowX: 'auto',
            }}
          >
            <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow sx={{ background: 'linear-gradient(135deg, #1976D2 0%, #0D47A1 100%)' }}>
                <TableCell sx={{ color: 'white', fontWeight: 700, textAlign: 'left' }}>Customer</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700, textAlign: 'left' }}>NIC</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700, textAlign: 'center' }}>Loan ID</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700, textAlign: 'center' }}>Installment</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700, textAlign: 'center' }}>Collected Date</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700, textAlign: 'center' }}>Collected Time</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700, textAlign: 'right' }}>Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {installments.map((installment) => {
                const customer = installment.loan?.customer;
                const collector = installment.collector;
                
                return (
                  <TableRow
                    key={installment.id}
                    sx={{
                      '&:nth-of-type(odd)': { backgroundColor: '#f5f5f5' },
                      '&:hover': { backgroundColor: '#e3f2fd' },
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PersonIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {customer?.fullName || 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {customer?.nic || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Chip
                        label={`#${installment.loan?.id || 'N/A'}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Chip
                        label={`#${installment.installmentNumber || 'N/A'}`}
                        size="small"
                        color="secondary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CalendarTodayIcon sx={{ mr: 0.5, fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          {installment.paidDate
                            ? new Date(installment.paidDate).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              })
                            : 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <AccessTimeIcon sx={{ mr: 0.5, fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          {formatTime(installment.updatedAt)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right' }}>
                      <Chip
                        label={formatCurrency(installment.paidAmount)}
                        size="small"
                        sx={{
                          background: 'linear-gradient(135deg, #2E7D32 0%, #1B5E20 100%)',
                          color: 'white',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default CollectedInstallments;

